import { parseISO } from 'date-fns'
import store from 'store'
import { Get } from 'type-fest'
import { blocks, plans } from '@opallabs/api/BFF'
import {
  boardObjectCollections,
  boards,
  OpalApiResponseDocument,
  OpalApiV3Component,
} from '@opallabs/api/v3'
import { CATEGORY_KINDS, ISO8601Duration } from '@opallabs/concepts'
import { sortResourcesByDateRelevancy } from '@opallabs/lambda'
import {
  PlanOption,
  BoardOption,
  SwimlaneOption,
  CollectionOption,
  MigrationPayload,
} from './types'

type SwimlaneList = Array<
  SwimlaneOption & { categoryPosition: number; swimlanePosition: number }
>

type PlanResponse = OpalApiResponseDocument<'ExperimentalReadPlanBFFV1', 200>

type SwimlaneHierarchyNode = Get<
  PlanResponse,
  ['data', 'categories', 'hierarchy']
>[number]

/**
 * Need to specify that a swimlane may not exist if you attempt to look it up by id. Our API docs
 * type the object as if its always present - need to update our tsconfig and openapi generation lib
 * to get that level of safety.
 */
type SwimlaneDefs = Record<
  string,
  Get<PlanResponse, ['data', 'categories', 'by_id']>[string] | undefined
>

const reduceSwimlanes =
  (swimlaneDefs: SwimlaneDefs, parent_category_position = 0) =>
  (
    swimlaneList: SwimlaneList,
    hierarchyNode: SwimlaneHierarchyNode
  ): SwimlaneList => {
    const swimlaneDef = swimlaneDefs[hierarchyNode.id]

    if (!swimlaneDef) return swimlaneList

    if (
      swimlaneDef.kind === CATEGORY_KINDS.graph_swimlane ||
      swimlaneDef.kind === CATEGORY_KINDS.smart_swimlane
    ) {
      return swimlaneList
    }

    // If this swimlane is a group, recurse over its children and add them to the list
    if (swimlaneDef.kind === CATEGORY_KINDS.group_swimlane) {
      // Our docs aren't great at recursive types until we update, so we need to reassert that the children are
      // the top level hierarchy type, otherwise we'd run into a `children: unknown[]` situation.
      return (hierarchyNode.children as Array<SwimlaneHierarchyNode>).reduce(
        reduceSwimlanes(swimlaneDefs, swimlaneDef.position),
        swimlaneList
      )
    }

    // If this swimlane is a top level swimlane and has a title, add it to the list
    if (swimlaneDef.kind === CATEGORY_KINDS.swimlane && swimlaneDef.title) {
      return [
        ...swimlaneList,
        {
          value: swimlaneDef.id,
          label: swimlaneDef.title,
          categoryPosition: parent_category_position,
          swimlanePosition: swimlaneDef.position,
        },
      ]
    }

    return swimlaneList
  }

// Extract swimlane options from plan response
export const extractSwimlaneOptions = (
  document: PlanResponse
): SwimlaneOption[] => {
  if (!document || !document.data) {
    return []
  }

  const swimlaneList = document.data.categories.hierarchy.reduce(
    reduceSwimlanes(document.data.categories.by_id),
    [] satisfies SwimlaneList
  )

  // Sort by category position, then by swimlane position
  return swimlaneList
    .sort((a, b) => {
      if (a.categoryPosition !== b.categoryPosition) {
        return a.categoryPosition - b.categoryPosition
      }
      return a.swimlanePosition - b.swimlanePosition
    })
    .map((item) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { categoryPosition, swimlanePosition, ...option } = item
      return option
    })
}

// Fetch swimlane options for a plan
export const fetchSwimlaneOptions = async (
  planId: string,
  workspaceUuid: string
): Promise<SwimlaneOption[]> => {
  const response = await plans.readSingle({
    params: {
      path: {
        plan_id: planId,
        workspace_id: workspaceUuid,
      },
    },
  })
  return extractSwimlaneOptions(response)
}

// Fetch collection options for a board
export const fetchCollectionOptions = async (
  boardId: string
): Promise<CollectionOption[]> => {
  const responseDocument = await boardObjectCollections.readCollection({
    query: {
      filter: {
        board_id: boardId,
      },
    },
  })

  return responseDocument.data
    .sort((a, b) => a.attributes.position - b.attributes.position)
    .map((collection) => ({
      value: collection.id,
      label: collection.attributes.title,
      collection: {
        id: collection.id,
        title: collection.attributes.title,
        position: collection.attributes.position,
      },
    }))
}

// Fetch browsable plan-blocks for the workspace. We query blocks (rather than
// plans) so we can filter by `browsable`, which is the canonical signal for
// "shows up in the Plans Directory" — see PlansView/usePlanDirectoryData.
export const fetchPlansData = async (
  workspaceUuid: string
): Promise<OpalApiResponseDocument<'ReadBlocksBFFV1', 200>['data']> => {
  const response = await blocks.readCollection({
    params: {
      path: { workspace_id: workspaceUuid },
      query: {
        filter: { browsable: true, has_child_view: true },
      },
    },
  })
  return response.data || []
}

// Fetch boards data
export const fetchBoardsData = async (
  workspaceUuid: string
): Promise<OpalApiV3Component<'board'>[]> => {
  const response = await boards.readCollection({
    params: {
      query: {
        filter: {
          workspace_id: [workspaceUuid],
        },
        fields: {
          board: 'title,created_at',
        },
      },
    },
  })
  return response.data || []
}

// Process browsable plan-blocks into plan options
export const processPlanOptions = (
  blocksData: OpalApiResponseDocument<'ReadBlocksBFFV1', 200>['data'],
  storyStartDate: string | null,
  storyEndDate: string | null
): PlanOption[] => {
  if (!blocksData) return []

  const momentStartDate = storyStartDate ? new Date(storyStartDate) : null
  const momentEndDate = storyEndDate ? new Date(storyEndDate) : null

  const blocksForSorting = blocksData.flatMap((block) => {
    if (!block.child_view_id) return []

    const startAt = block.start_date
      ? parseISO(block.start_date)
      : parseISO(block.created_at)
    const endAt =
      block.start_date && block.duration
        ? ISO8601Duration.add(parseISO(block.start_date), block.duration)
        : startAt

    return [
      {
        planId: block.child_view_id,
        title: block.title,
        startAt,
        endAt,
        updatedAt: parseISO(block.last_updated_date),
      },
    ]
  })

  const sortedBlocks = sortResourcesByDateRelevancy(
    blocksForSorting,
    momentStartDate,
    momentEndDate
  )

  return sortedBlocks.map((block) => ({
    value: block.planId,
    label: block.title,
    plan: {
      id: block.planId,
      title: block.title,
    },
  }))
}

// Process boards data into options
export const processBoardOptions = (
  boardsData: OpalApiV3Component<'board'>[]
): BoardOption[] => {
  if (!boardsData) return []

  return boardsData.map((board) => ({
    value: board.id,
    label: board.attributes.title,
    board: {
      id: board.id,
      title: board.attributes.title,
    },
  }))
}

// Submit migration request
export const submitMigration = async (
  storyId: string,
  payload: MigrationPayload
): Promise<Response> => {
  const response = await fetch(`/stories/v3/${storyId}/migrate_moments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token':
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content') || '',
      'Session-Token': store.get('accessToken') || '',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.message || `Migration failed with status ${response.status}`
    )
  }

  return response
}
